import { LatLng, LatLngBounds, Map, point } from "leaflet";
import type { INFO_CARD_TYPE } from "@/features/cards/shared/card-routing/card-types";
import { getEffectiveChipsHeight, getNavbarHeight } from "@/features/cards/shared/card-shell/card-geometry";
import { useStore } from "@/store/store";
import GlobalOptions from "@/utils/global-options";
import {
    DEFAULT_BOUNDS_PAD_RATIO,
    DEFAULT_BOUNDS_ZOOM_DIFF_THRESHOLD,
    DEFAULT_POINT_FOCUS_ZOOM,
    MIN_FITTING_DIMENSION_PX,
    MOBILE_PAN_OFFSET,
} from "./pan-constants";

/** Shared animation options for all map pans. */
const PAN_ANIMATION = { duration: 0.5 } as const;

interface ResolveTargetZoomFromIdealProps {
    currentZoom: number;
    idealZoom: number;
    /** @default DEFAULT_BOUNDS_ZOOM_DIFF_THRESHOLD */
    zoomDiffThreshold?: number;
}

/**
 * Zoom policy shared by bounds and point focus
 * Zoom in to ideal when zoom differs by at least the threshold - then zoom in to fit
 * Zoom out to ideal when we are zoomed in more than ideal - then zoom out to fit
 */
export const resolveTargetZoomFromIdeal = ({
    currentZoom,
    idealZoom,
    zoomDiffThreshold = DEFAULT_BOUNDS_ZOOM_DIFF_THRESHOLD,
}: ResolveTargetZoomFromIdealProps): number => {
    const needsZoomOut = currentZoom > idealZoom;
    const shouldAdjustZoom = needsZoomOut || Math.abs(idealZoom - currentZoom) >= zoomDiffThreshold;
    return shouldAdjustZoom ? idealZoom : currentZoom;
};

interface BuildObscurationContextProps {
    mapComponent: Map;
    rightCardWidth: number;
    rightCardHeight: number;
    leftCardWidth: number;
    leftCardHeight: number;
    /** Current chips bar height — used to compute the viewable area's top boundary. */
    chipsHeight: number;
    /** Card type from the right slot — drives chips visibility logic. */
    cardTypeInRightSlot: INFO_CARD_TYPE | undefined;
    paddingX: number;
    paddingY: number;
    buffer: number;
    /** Degrees of latitude per pixel at the target zoom. */
    latitudePerPixel: number;
    /** Degrees of longitude per pixel at the target zoom. */
    longitudePerPixel: number;
}

// Determines what is obscured by the cards and how much to pan the map to avoid it
const buildObscurationContext = ({
    mapComponent,
    rightCardWidth,
    rightCardHeight,
    leftCardWidth,
    leftCardHeight,
    chipsHeight,
    cardTypeInRightSlot,
    paddingX,
    paddingY,
    buffer,
    latitudePerPixel,
    longitudePerPixel,
}: BuildObscurationContextProps) => {
    const effectiveChipsHeight = getEffectiveChipsHeight(chipsHeight, cardTypeInRightSlot);

    // Apply padding to the right card width, used for the left-edge-of-menu calculation
    const paddedRightCardWidth = rightCardWidth + paddingX;

    // In event of multiple cards, the tallest bottom card determines the obscured zone.
    // paddingY is added after the max so both cards are treated symmetrically.
    const effectiveBottomHeight = Math.max(rightCardHeight, leftCardHeight) + paddingY;

    const mapBounds = mapComponent.getBounds();
    const bottomRightLatLng = mapBounds.getSouthEast();
    const bottomRightContainerPoint = mapComponent.latLngToContainerPoint(bottomRightLatLng);

    // The top edge of the tallest bottom card (right or left)
    const topEdgeOfTallest = bottomRightContainerPoint.y - effectiveBottomHeight - buffer;
    // The left (inner) edge of the right card
    const leftEdgeOfRightCard = bottomRightContainerPoint.x - paddedRightCardWidth - buffer;
    // The right (inner) edge of the left card
    const rightEdgeOfLeftCard = leftCardWidth + buffer;

    // The center between the chips bar and the tallest bottom card
    const panOffsetLat = (latitudePerPixel * (effectiveBottomHeight + buffer - effectiveChipsHeight)) / 2.0;

    /**
     * The net horizontal shift to center in the viewable area between both cards.
     * Positive shifts east (the right card is larger), negative shifts west (the left card is larger)
     */
    const panOffsetLng = (longitudePerPixel * (paddedRightCardWidth - leftCardWidth)) / 2;

    return {
        topEdgeOfTallest,
        leftEdgeOfRightCard,
        rightEdgeOfLeftCard,
        panOffsetLat,
        panOffsetLng,
    };
};

interface ComputeAdjustedCenterProps {
    baseLatLng: LatLng;
    containerPoint: { x: number; y: number };
    /**
     * Point used for the left-card X obscuration check. Defaults to containerPoint when absent.
     * For bounds panning, pass the SouthWest container point (lowest X) so we test the left edge of the
     * bounds against the left card
     */
    containerPointForLeft?: { x: number; y: number };
    topEdgeOfTallest: number;
    leftEdgeOfRightCard: number;
    rightEdgeOfLeftCard: number;
    panOffsetLat: number;
    panOffsetLng: number;
    /** True when the right-side card should be avoided on the X axis. */
    avoidX: boolean;
    /** True when the left-side card should be avoided on the X axis. */
    avoidLeftX: boolean;
    avoidY: boolean;
    /**
     * When true, applies the centering offset unconditionally regardless of whether the
     * entity is currently obscured. Also used at call sites to guard the flyTo call itself.
     */
    shouldPanRegardlessOfObscuration: boolean;
}

// Computes the adjusted center point to avoid both the right and (optionally) left cards
const computeAdjustedCenter = ({
    baseLatLng,
    containerPoint,
    containerPointForLeft,
    topEdgeOfTallest,
    leftEdgeOfRightCard,
    rightEdgeOfLeftCard,
    panOffsetLat,
    panOffsetLng,
    avoidX,
    avoidLeftX,
    avoidY,
    shouldPanRegardlessOfObscuration,
}: ComputeAdjustedCenterProps) => {
    const leftPoint = containerPointForLeft ?? containerPoint;
    const isObscuredY = avoidY && containerPoint.y > topEdgeOfTallest;
    const isObscuredX = avoidX && containerPoint.x > leftEdgeOfRightCard;
    const isObscuredByLeftCard = avoidLeftX && leftPoint.x < rightEdgeOfLeftCard;

    const targetLat =
        avoidY && (isObscuredY || shouldPanRegardlessOfObscuration) ? baseLatLng.lat - panOffsetLat : baseLatLng.lat;

    /**
     * Apply the X offset whenever either card is being avoided and the entity is (or will be) obscured.
     * panOffsetLng already accounts for both cards so applying it once is sufficient.
     */
    const xObscured = isObscuredX || isObscuredByLeftCard;
    const targetLng =
        (avoidX || avoidLeftX) && (xObscured || shouldPanRegardlessOfObscuration)
            ? baseLatLng.lng + panOffsetLng
            : baseLatLng.lng;

    const shouldFlyTo =
        (avoidX && isObscuredX) ||
        (avoidY && isObscuredY) ||
        (avoidLeftX && isObscuredByLeftCard) ||
        shouldPanRegardlessOfObscuration;

    return { targetLat, targetLng, shouldFlyTo };
};

const COORD_THRESHOLD = 1e-5; // Represents ~1 metre — effectively sub-pixel at all practical zoom levels
const ZOOM_THRESHOLD = 0.01; // Represents any change in zoom at all

// Returns true when the map can be considered to be already at the target position and zoom
const isAlreadyAtTarget = (mapComponent: Map, targetLat: number, targetLng: number, targetZoom: number): boolean => {
    const { lat, lng } = mapComponent.getCenter();
    return (
        Math.abs(lat - targetLat) < COORD_THRESHOLD &&
        Math.abs(lng - targetLng) < COORD_THRESHOLD &&
        Math.abs(mapComponent.getZoom() - targetZoom) < ZOOM_THRESHOLD
    );
};

// Compute degrees-per-pixel at a given zoom around a reference LatLng using project/unproject
const computeDegreesPerPixelAtZoom = (
    mapComponent: Map,
    reference: LatLng,
    zoom: number,
): { latitudePerPixel: number; longitudePerPixel: number } => {
    const refPoint = mapComponent.project(reference, zoom);
    const atRef = mapComponent.unproject(point(refPoint.x, refPoint.y), zoom);
    const latAtRefPlus = mapComponent.unproject(point(refPoint.x, refPoint.y + 100), zoom).lat;
    const lngAtRefPlus = mapComponent.unproject(point(refPoint.x + 100, refPoint.y), zoom).lng;
    return {
        latitudePerPixel: (atRef.lat - latAtRefPlus) / 100,
        longitudePerPixel: (lngAtRefPlus - atRef.lng) / 100,
    };
};

interface CenterPointInViewableAreaProps {
    mapComponent: Map;
    /** Right-side card (e.g. device card on desktop). */
    rightCardWidth: number;
    rightCardHeight: number;
    /** Left-side card (e.g. search card on desktop). */
    leftCardWidth?: number;
    leftCardHeight?: number;
    /** Current chips bar height — determines the viewable area's top boundary. */
    chipsHeight: number;
    /** Card type in the right slot, determines if chips are visible. */
    cardTypeInRightSlot: INFO_CARD_TYPE | undefined;
    latLng: LatLng;
    paddingX?: number;
    paddingY?: number;
    buffer?: number;
    shouldPanRegardlessOfObscuration?: boolean;
    avoidX?: boolean;
    avoidLeftX?: boolean;
    avoidY?: boolean;
    /** When set, uses this zoom as-is (threshold does not apply). */
    newZoom?: number;
    zoomDiffThreshold?: number;
}

/**
 * Pans the map to center the point in the viewable rectangle (the area not obscured by any card).
 *
 */
export const centerPointInViewableArea = ({
    mapComponent,
    rightCardWidth,
    rightCardHeight,
    leftCardWidth = 0,
    leftCardHeight = 0,
    chipsHeight,
    cardTypeInRightSlot,
    latLng,
    paddingX = 0,
    paddingY = 0,
    buffer = MOBILE_PAN_OFFSET,
    shouldPanRegardlessOfObscuration = false,
    avoidX = true,
    avoidLeftX = false,
    avoidY = true,
    newZoom,
    zoomDiffThreshold = DEFAULT_BOUNDS_ZOOM_DIFF_THRESHOLD,
}: CenterPointInViewableAreaProps) => {
    const targetPointZoom = resolveTargetZoomFromIdeal({
        currentZoom: mapComponent.getZoom(),
        idealZoom: DEFAULT_POINT_FOCUS_ZOOM,
        zoomDiffThreshold,
    });
    const targetZoom = newZoom ?? targetPointZoom;
    const { latitudePerPixel, longitudePerPixel } = computeDegreesPerPixelAtZoom(mapComponent, latLng, targetZoom);

    const { topEdgeOfTallest, leftEdgeOfRightCard, rightEdgeOfLeftCard, panOffsetLat, panOffsetLng } =
        buildObscurationContext({
            mapComponent,
            rightCardWidth,
            rightCardHeight,
            leftCardWidth,
            leftCardHeight,
            chipsHeight,
            cardTypeInRightSlot,
            paddingX,
            paddingY,
            buffer,
            latitudePerPixel,
            longitudePerPixel,
        });

    const targetContainerPoint = mapComponent.latLngToContainerPoint(latLng);
    const { targetLat, targetLng, shouldFlyTo } = computeAdjustedCenter({
        baseLatLng: latLng,
        containerPoint: targetContainerPoint,
        topEdgeOfTallest,
        leftEdgeOfRightCard,
        rightEdgeOfLeftCard,
        panOffsetLat,
        panOffsetLng,
        avoidX,
        avoidLeftX,
        avoidY,
        shouldPanRegardlessOfObscuration,
    });

    if (shouldFlyTo && !isAlreadyAtTarget(mapComponent, targetLat, targetLng, targetZoom)) {
        mapComponent.flyTo({ lat: targetLat, lng: targetLng }, targetZoom, PAN_ANIMATION);
    }
};

interface ComputeBoundsZoomPaddingProps {
    rightCardWidth: number;
    rightCardHeight: number;
    leftCardWidth: number;
    leftCardHeight: number;
    chipsHeight: number;
    cardTypeInRightSlot: INFO_CARD_TYPE | undefined;
    avoidX: boolean;
    avoidLeftX: boolean;
    avoidY: boolean;
    paddingX: number;
    paddingY: number;
    buffer: number;
}

/**
 * Computes card-aware padding so getBoundsZoom fits the bounds within the viewable area
 * (the portion of the map not covered by any card).
 */
const computeBoundsZoomPadding = ({
    rightCardWidth,
    rightCardHeight,
    leftCardWidth,
    leftCardHeight,
    chipsHeight,
    cardTypeInRightSlot,
    avoidX,
    avoidLeftX,
    avoidY,
    paddingX,
    paddingY,
    buffer,
}: ComputeBoundsZoomPaddingProps) => {
    const effectiveChipsHeight = getEffectiveChipsHeight(chipsHeight, cardTypeInRightSlot);
    // paddingY applied after max so both cards are treated symmetrically (mirrors buildObscurationContext)
    const effectiveBottomObscuration = avoidY ? Math.max(rightCardHeight, leftCardHeight) + paddingY + buffer : 0;
    const effectiveTopObscuration = avoidY ? effectiveChipsHeight : 0;
    const effectiveRightObscuration = avoidX ? rightCardWidth + paddingX + buffer : 0;
    const effectiveLeftObscuration = avoidLeftX ? leftCardWidth + buffer : 0;

    // Leaflet getBoundsZoom does size = getSize().subtract(padding) once per axis, so these must be
    // total horizontal and vertical obscuration (not half per side).
    const boundsZoomPaddingX = effectiveRightObscuration + effectiveLeftObscuration;
    const boundsZoomPaddingY = effectiveBottomObscuration + effectiveTopObscuration;

    return { boundsZoomPaddingX, boundsZoomPaddingY };
};

interface CenterBoundsInViewableAreaProps {
    mapComponent: Map;
    bounds: LatLngBounds;
    /** Right-side card (e.g. device card on desktop). */
    rightCardWidth: number;
    rightCardHeight: number;
    /** Left-side card (e.g. search card on desktop). */
    leftCardWidth?: number;
    leftCardHeight?: number;
    /** Current chips bar height — determines the viewable area's top boundary. */
    chipsHeight: number;
    /** Card type in the right slot, determines if chips are visible. */
    cardTypeInRightSlot: INFO_CARD_TYPE | undefined;
    paddingX?: number;
    paddingY?: number;

    /** Pixel buffer manually applied to cards, acts like padding */
    buffer?: number;
    shouldPanRegardlessOfObscuration?: boolean;
    /** Fraction to pad bounds before computing visibility. Set to 0 to disable. @default DEFAULT_BOUNDS_PAD_RATIO */
    padRatio?: number;
    /** Pan to fit the bounds zoom only when zoom difference exceeds this threshold. Always zooms out when too zoomed in. @default DEFAULT_BOUNDS_ZOOM_DIFF_THRESHOLD */
    zoomDiffThreshold?: number;
    avoidX?: boolean;
    avoidLeftX?: boolean;
    avoidY?: boolean;
}

/**
 * Pans the map to center the bounds in the viewable rectangle (the area not obscured by any card).
 */
export const centerBoundsInViewableArea = ({
    mapComponent,
    bounds,
    rightCardWidth,
    rightCardHeight,
    leftCardWidth = 0,
    leftCardHeight = 0,
    chipsHeight,
    cardTypeInRightSlot,
    paddingX = 0,
    paddingY = 0,
    buffer = MOBILE_PAN_OFFSET,
    shouldPanRegardlessOfObscuration = true,
    padRatio = DEFAULT_BOUNDS_PAD_RATIO,
    zoomDiffThreshold = DEFAULT_BOUNDS_ZOOM_DIFF_THRESHOLD,
    avoidX = true,
    avoidLeftX = false,
    avoidY = true,
}: CenterBoundsInViewableAreaProps) => {
    const boundsForVisibility = padRatio !== 0 ? bounds.pad(padRatio) : bounds;
    const southEastContainerPoint = mapComponent.latLngToContainerPoint(boundsForVisibility.getSouthEast());
    // SW corner has the minimum X — used to check left-card obscuration (SE's high X would never trigger it)
    const southWestContainerPoint = mapComponent.latLngToContainerPoint(boundsForVisibility.getSouthWest());
    const boundsCenter = boundsForVisibility.getCenter();

    const currentZoom = mapComponent.getZoom();

    const { boundsZoomPaddingX, boundsZoomPaddingY } = computeBoundsZoomPadding({
        rightCardWidth,
        rightCardHeight,
        leftCardWidth,
        leftCardHeight,
        chipsHeight,
        cardTypeInRightSlot,
        avoidX,
        avoidLeftX,
        avoidY,
        paddingX,
        paddingY,
        buffer,
    });

    // Zoom only if there is enough space to fit the bounds within the viewable map area. Otherwise, keep current zoom and only pan.
    const mapSize = mapComponent.getSize();
    const hasEnoughSpace =
        mapSize.x - boundsZoomPaddingX >= MIN_FITTING_DIMENSION_PX &&
        mapSize.y - boundsZoomPaddingY >= MIN_FITTING_DIMENSION_PX;
    const boundsZoom = hasEnoughSpace
        ? mapComponent.getBoundsZoom(boundsForVisibility, false, point(boundsZoomPaddingX, boundsZoomPaddingY))
        : currentZoom;
    const targetZoom = resolveTargetZoomFromIdeal({
        currentZoom,
        idealZoom: boundsZoom,
        zoomDiffThreshold,
    });

    const { latitudePerPixel, longitudePerPixel } = computeDegreesPerPixelAtZoom(
        mapComponent,
        boundsCenter,
        targetZoom,
    );

    const { topEdgeOfTallest, leftEdgeOfRightCard, rightEdgeOfLeftCard, panOffsetLat, panOffsetLng } =
        buildObscurationContext({
            mapComponent,
            rightCardWidth,
            rightCardHeight,
            leftCardWidth,
            leftCardHeight,
            chipsHeight,
            cardTypeInRightSlot,
            paddingX,
            paddingY,
            buffer,
            latitudePerPixel,
            longitudePerPixel,
        });

    const { targetLat, targetLng, shouldFlyTo } = computeAdjustedCenter({
        baseLatLng: boundsCenter,
        containerPoint: southEastContainerPoint,
        containerPointForLeft: southWestContainerPoint,
        topEdgeOfTallest,
        leftEdgeOfRightCard,
        rightEdgeOfLeftCard,
        panOffsetLat,
        panOffsetLng,
        avoidX,
        avoidLeftX,
        avoidY,
        shouldPanRegardlessOfObscuration,
    });

    if (shouldFlyTo && !isAlreadyAtTarget(mapComponent, targetLat, targetLng, targetZoom)) {
        mapComponent.flyTo({ lat: targetLat, lng: targetLng }, targetZoom, PAN_ANIMATION);
    }
};

// Manually measured constants for device-group menu panning (height is local, not in the zustand store)
const DEVICE_GROUP_MOBILE_PAN_OFFSET = 50;
const DEVICE_GROUP_MENU_BOTTOM_PADDING = 15;

interface CenterInViewableAreaProps {
    mapComponent: Map;
    cardHeight: number;
    iconLatLng: LatLng;
    padding?: number;
    buffer?: number;
    shouldAlwaysPan?: boolean;
}

/**
 * Pans the map to make the icon visible above a bottom element (e.g. device group menu) whose
 * height is computed locally and not stored in zustand. For cases where the obscuring element IS
 * in the store, use panTo() instead.
 */
export const centerInViewableArea = ({
    mapComponent,
    cardHeight,
    iconLatLng,
    padding = DEVICE_GROUP_MENU_BOTTOM_PADDING,
    buffer = DEVICE_GROUP_MOBILE_PAN_OFFSET,
    shouldAlwaysPan = false,
}: CenterInViewableAreaProps) => {
    const navbarHeight = getNavbarHeight();
    const chipsHeight = useStore.getState().chipsHeight;
    const actualChipsHeight = GlobalOptions.showChipsAtSameTimeAsInfoCards ? chipsHeight : 0;
    const totalHeight = cardHeight + padding + navbarHeight;

    const mapBounds = mapComponent.getBounds();
    const bottomRightLatLng = mapBounds.getSouthEast();
    const bottomRightContainerPoint = mapComponent.latLngToContainerPoint(bottomRightLatLng);

    const { lat: latOfTopOfMenu } = mapComponent.containerPointToLatLng([
        0,
        bottomRightContainerPoint.y - totalHeight - buffer,
    ]);

    const latitudePerPixel =
        (mapComponent.containerPointToLatLng([0, 0]).lat - mapComponent.containerPointToLatLng([0, 100]).lat) / 100;

    const isIconObscured = iconLatLng.lat < latOfTopOfMenu;

    if (isIconObscured || shouldAlwaysPan) {
        const panOffset = (latitudePerPixel * (totalHeight - actualChipsHeight)) / 2.0;
        const targetLat = iconLatLng.lat - panOffset;
        mapComponent.panTo({ lat: targetLat, lng: iconLatLng.lng });
    }
};
